import { Identifier } from 'src/types/utilityTypes';
import api from 'src/utils/ajax';
import { dateToTimestamp, timestampToDate } from 'src/utils/dateFormatter';
import { decamelize } from 'src/utils/decamelize';

import { normalize } from '../shared/normalizers';
import { User } from '../user/_types';

import {
  Group,
  GroupCalendar,
  GroupExerciseLeaderboard,
  GroupLeaderboard,
  GroupMeasurementLeaderboard,
  GroupMemberSearchResult,
  GroupMembership,
  GroupWodLeaderboard,
} from './_types';
import {
  denormalizeGroup,
  normalizeGroupMemberSearchResult,
  normalizeGroupSearchResult,
  normalizeMyGroups,
} from './normalizers';

export const fetchGroups = async ({
  sortBy,
  sortDir,
  page = 0,
  perPage = 20,
  search = '',
  isPublic = false,
}: {
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  page?: number;
  perPage?: number;
  search?: string;
  isPublic?: boolean;
}): Promise<{ groups: Group[]; total: number }> => {
  try {
    const response = await api.get('/api/v2/groups', {
      params: {
        page,
        per: perPage,
        ...(search && { 'q[name_cont]': search }),
        ...(sortBy && {
          'q[s]': `${decamelize(sortBy || '')} ${sortDir || 'desc'}`,
        }),
        ...(isPublic && { public: true }),
      },
    });
    const normalizedGroups = response.data?.group?.map((group) =>
      normalize(group)
    );

    return { groups: normalizedGroups, total: response.data?.meta?.total };
  } catch (error) {
    throw new Error(api.formatError(error, 'Error fetching groups.'));
  }
};
export const saveGroup = async ({
  keepFields,
  ...group
}: Group & { keepFields?: string[] }): Promise<Group> => {
  const data = denormalizeGroup(group, keepFields);
  const response = group.id
    ? await api.put(`/api/v3/groups/${group.id}`, { group: data })
    : await api.post('/api/v3/groups', { group: data });
  return normalize(response.data);
};

export const handleFileUpload = async (
  file: File
): Promise<{ id: string; url: string }> => {
  const formData = new FormData();
  formData.append('asset_image[image]', file);
  const response = await api.post('/api/v2/assets/image/group', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const fetchClientGroups = async (
  userId: User['id']
): Promise<GroupMembership[]> => {
  const response = await api.get('/api/v3/group_members', {
    params: {
      user_id: userId,
      fetch_all: true,
    },
  });

  return response.data.map((membership) => ({
    ...(normalize(membership) as GroupMembership),
    createdAt: timestampToDate(membership.created_at),
    updatedAt: timestampToDate(membership.updated_at),
  }));
};

export const fetchAllGroups = async (platformId?: string): Promise<Group[]> => {
  const response = await api.get('/api/v2/groups', {
    params: {
      fetch_all: true,
      with_stripe: true,
      'q[s]': 'name asc',
      ...(platformId && { platform_id: platformId }),
    },
  });

  return response.data.map(normalize);
};

export const fetchAllGroupsByPluckColumns = async (
  pluckColumns: string,
  platformId?: string
): Promise<Group[]> => {
  const response = await api.get('/api/v2/groups', {
    params: {
      fetch_all: true,
      pluck_columns: pluckColumns,
      'q[s]': 'name asc',
      ...(platformId && { platform_id: platformId }),
    },
  });

  return response.data.map(normalize);
};

interface FetchGroupOptions {
  id: Group['id'];
}

export const fetchGroup = async ({ id }: FetchGroupOptions): Promise<Group> => {
  const response = await api.get(`/api/v3/groups/${id}`);
  return normalize(response.data);
};

export const fetchGroupsByParams = async (params = {}): Promise<Group[]> => {
  const response = await api.get(`/api/v3/groups`, {
    params: { ...params },
  });
  return response.data.map(normalize);
};

export const fetchGroupCalendars = async (): Promise<GroupCalendar[]> => {
  const response = await api.get('/api/v3/groups/calendars');
  return response.data.map(normalize);
};

export const removeFromGroup = async (
  membershipId: GroupMembership['id']
): Promise<void> => {
  await api.delete(`/api/v3/group_members/${membershipId}`);
};

export const addToGroup = async (
  userId: User['id'],
  groupId: Group['id']
): Promise<GroupMembership> => {
  const response = await api.post('/api/v3/group_members', {
    user_id: userId,
    group_id: groupId,
  });

  return normalize(response.data);
};

export const cloneGroup = async (groupId: Group['id']): Promise<Group> => {
  return await api.get(`/api/v2/groups/${groupId}/clone`);
};

export const deleteGroup = async (groupId: Group['id']): Promise<void> => {
  return await api.delete(`/api/v3/groups/${groupId}`);
};
export const fetchUserGroups = async (params: {
  for_user?: boolean;
  member_only?: boolean;
  ids?: number[];
}): Promise<Group[]> => {
  try {
    const response = await api.get('/api/v2/groups', {
      params: {
        fetch_all: true,
        ...params,
      },
    });
    return response.data.map(normalizeMyGroups);
  } catch (error) {
    throw new Error(api.formatError(error, 'Error fetching user groups.'));
  }
};

export const searchGroupResults = async ({
  search,
  trainerId,
  signal,
}: {
  search: string;
  trainerId: Identifier;
  signal?: AbortSignal;
}): Promise<GroupMemberSearchResult[]> => {
  const response = await api.get('/sm/search', {
    signal,
    params: {
      types: [`group_white_label_${trainerId}`],
      limit: 20,
      no_limit: false,
      term: search,
      page: 1,
    },
  });
  return response.data.results[`group_white_label_${trainerId}`].map(
    (r: GroupMemberSearchResult) => normalizeGroupSearchResult(r)
  );
};

export const fetchGroupLeaderboard = async ({
  groupId,
  startDate,
  endDate,
  page,
  per,
  sort,
  userId,
}: {
  groupId: Group['id'];
  startDate?: string;
  endDate?: string;
  page?: number;
  per?: number;
  sort?: string;
  userId?: string;
}): Promise<GroupLeaderboard[]> => {
  const response = await api.get(`/api/v3/groups/${groupId}/leaderboard`, {
    params: {
      start_date: startDate ? dateToTimestamp(new Date(startDate)) : undefined,
      end_date: endDate ? dateToTimestamp(new Date(endDate)) : undefined,
      page,
      per,
      sort,
      user_id: userId,
    },
  });
  return normalize(response.data.leaderboard);
};

export interface GroupMeasurementLeaderboardResponse {
  leaderboard: GroupMeasurementLeaderboard[];
  total: number;
}

export const fetchGroupMeasurementLeaderboard = async ({
  groupId,
  startDate,
  endDate,
  metric,
  page,
  per,
  sort,
}: {
  groupId: Group['id'];
  startDate?: string;
  endDate?: string;
  metric: string;
  page?: number;
  per?: number;
  sort?: string;
}): Promise<GroupMeasurementLeaderboardResponse> => {
  const response = await api.get(
    `/api/v3/groups/${groupId}/measurement_leaderboard/`,
    {
      params: {
        start_date: startDate
          ? dateToTimestamp(new Date(startDate))
          : undefined,
        end_date: endDate
          ? dateToTimestamp(new Date(endDate))
          : undefined,
        stat: metric,
        page,
        per,
        sort,
      },
    }
  );

  return {
    leaderboard: normalize(response.data.leaderboard || []) || [],
    total: response.data.meta.total || 0,
  };
};

export interface GroupWodLeaderboardResponse {
  leaderboard: GroupWodLeaderboard[];
  total: number;
}

export const fetchGroupWodLeaderboard = async ({
  groupId,
  startDate,
  gender,
  page,
  per,
}: {
  groupId: Group['id'];
  startDate?: string;
  gender?: string | null;
  page?: number;
  per?: number;
}): Promise<GroupWodLeaderboardResponse> => {
  const response = await api.get(`/api/v3/groups/${groupId}/wod_leaderboard/`, {
    params: {
      start_date: startDate ? dateToTimestamp(new Date(startDate)) : undefined,
      gender,
      page,
      per,
    },
  });

  return {
    leaderboard: normalize(response.data.leaderboard || []) || [],
    total: response.data.meta.total || 0,
  };
};

export interface GroupExerciseLeaderboardResponse {
  leaderboard: GroupExerciseLeaderboard[];
  total: number;
}

export const fetchGroupExerciseLeaderboard = async ({
  groupId,
  startDate,
  endDate,
  page,
  per,
  sort,
  exerciseId,
}: {
  groupId: Group['id'];
  startDate?: string;
  endDate?: string;
  page?: number;
  per?: number;
  sort?: string;
  exerciseId: string;
}): Promise<GroupExerciseLeaderboardResponse> => {
  const response = await api.get(
    `/api/v3/groups/${groupId}/exercise_leaderboard/`,
    {
      params: {
        start_date: startDate
          ? dateToTimestamp(new Date(startDate))
          : undefined,
        end_date: endDate ? dateToTimestamp(new Date(endDate)) : undefined,
        page,
        per,
        sort,
        exercise_id: exerciseId,
      },
    }
  );

  return {
    leaderboard: normalize(response.data.leaderboard || []) || [],
    total: response.data.total || 0,
  };
};

interface searchGroupMembersOptions {
  search: string;
  types: string[];
  noLimit?: boolean;
  signal?: AbortSignal;
}

export const searchGroupMembers = async ({
  search,
  types = [],
  noLimit = false,
  signal,
}: searchGroupMembersOptions) => {
  const response = await api.get('/api/v4/autocomplete', {
    signal,
    params: {
      types,
      limit: 10,
      no_limit: noLimit,
      term: search,
      page: 1,
    },
  });
  return response.data.results[types[0]].map((r) =>
    normalizeGroupMemberSearchResult(r)
  );
};
